// frontend/src/pages/freelancer/FreelancerEarnings.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import axios from "axios";
import "./freelancerStyles.css";

// Maps canonical PayoutLedgerEntry fields → booking-shaped objects the
// held/released render sections expect. R7 earns from service bookings.
const normalizeCanonical = (rec) => {
  const sl = (t) =>
    ({
      booking_service: "Service Booking",
      booking_product: "Product Booking",
      booth_rent: "Booth Rent",
      package_session: "Package — Session",
      manual: "Manual",
    })[t] ||
    t ||
    "Earning";
  const mapEntry = (e) => ({
    _id: e._id,
    customerName: sl(e.sourceType),
    professionalEarnings: e.netEligibleAmount || 0,
    grossAmount: e.grossAmount || 0,
    completedAt: e.createdAt,
    autoReleaseAt: e.autoReleaseAt,
    payoutReleasedAt: e.payoutReleasedAt,
  });
  return {
    held: (rec.held?.entries || []).map(mapEntry),
    released: (rec.released?.entries || []).map(mapEntry),
    history: rec.history || [],
    heldTotal: rec.held?.total || 0,
    releasedTotal: rec.released?.total || 0,
    totalPaidThisMonth: rec.totalPaidThisMonth || 0,
    nextPayoutDate: rec.nextPayoutDate || null,
  };
};

function FreelancerEarnings() {
  const navigate = useNavigate();
  const isBoothRenterRoute = window.location.pathname.includes(
    "/dashboard/booth-renter",
  );
  const roleCode = isBoothRenterRoute ? "R7" : "R4";
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState({
    total: 0,
    thisMonth: 0,
    lastMonth: 0,
    pending: 0,
    paid: 0,
  });
  const [payouts, setPayouts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [reconciliation, setReconciliation] = useState(null); // canonical only
  const [usingCanonical, setUsingCanonical] = useState(false);
  const [expanded, setExpanded] = useState({
    held: true,
    released: true,
    history: false,
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error.");
      return null;
    }
    return baseURL;
  }, []);

  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;

        setUsingCanonical(true);

        const [earningsRes, recRes, historyRes] = await Promise.all([
          axios.get(`${baseURL}/api/staff/earnings`, { headers }),
          fetch(`${baseURL}/api/payout-ledger/staff/reconciliation`, {
            headers,
          }),
          axios.get(`${baseURL}/api/payout-ledger/staff/history`, { headers }),
        ]);

        setEarnings(
          earningsRes.data || {
            total: 0,
            thisMonth: 0,
            lastMonth: 0,
            pending: 0,
            paid: 0,
          },
        );
        setTransactions(earningsRes.data.transactions || []);

        if (recRes.status === 401) {
          localStorage.removeItem("ogestyle_token");
          navigate("/login");
          return;
        }
        const recJson = recRes.ok ? await recRes.json() : null;
        setReconciliation(recJson ? normalizeCanonical(recJson) : null);
        setPayouts(historyRes.data?.payouts || []);
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem("ogestyle_token");
          navigate("/login");
        } else {
          setErrorMsg("Failed to load earnings");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchEarnings();
  }, [getAuthHeaders, getApiBaseUrl, navigate, roleCode]);

  const toggleSection = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const formatCountdown = (releaseDate) => {
    if (!releaseDate) return "—";
    const diff = new Date(releaseDate) - new Date();
    if (diff <= 0) return "Releasing soon...";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    return `${hours}h ${mins}m`;
  };

  const fmt = (n) => `$${(n || 0).toFixed(2)}`;

  if (loading)
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading earnings...</p>
        </div>
      </FreelancerSidebarLayout>
    );

  return (
    <FreelancerSidebarLayout>
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: isMobile ? "15px" : "20px",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "30px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div>
            <h2
              style={{
                margin: "0 0 8px 0",
                fontSize: isMobile ? "24px" : "28px",
              }}
            >
              My Earnings
            </h2>
            <p
              style={{
                margin: 0,
                color: "#666",
                fontSize: isMobile ? "14px" : "16px",
              }}
            >
              Track your income and payout history
            </p>
          </div>
          {usingCanonical && (
            <span
              style={{
                display: "inline-block",
                padding: "3px 12px",
                background: "#dcfce7",
                color: "#166534",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: "600",
                alignSelf: "center",
              }}
            >
              ✓ Live Ledger
            </span>
          )}
        </div>

        {errorMsg && (
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
          </div>
        )}

        {/* Earnings Summary Cards — always shown (from /api/staff/earnings) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "15px",
            marginBottom: "30px",
          }}
        >
          {[
            {
              label: "Total Earnings",
              value: `$${earnings.total?.toFixed(2) || "0.00"}`,
              color: "#001d4a",
              icon: "💰",
            },
            {
              label: "This Month",
              value: `$${earnings.thisMonth?.toFixed(2) || "0.00"}`,
              color: "#10b981",
              icon: "📈",
            },
            {
              label: "Last Month",
              value: `$${earnings.lastMonth?.toFixed(2) || "0.00"}`,
              color: "#6366f1",
              icon: "📊",
            },
            {
              label: "Pending",
              value: `$${earnings.pending?.toFixed(2) || "0.00"}`,
              color: "#f59e0b",
              icon: "⏳",
            },
            {
              label: "Paid Out",
              value: `$${earnings.paid?.toFixed(2) || "0.00"}`,
              color: "#10b981",
              icon: "✅",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="dashboard-card"
              style={{
                padding: isMobile ? "20px" : "25px",
                textAlign: "center",
                background: `${item.color}10`,
              }}
            >
              <div
                style={{
                  fontSize: isMobile ? "28px" : "32px",
                  marginBottom: "8px",
                }}
              >
                {item.icon}
              </div>
              <div
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: item.color,
                  marginBottom: "5px",
                }}
              >
                {item.value}
              </div>
              <div
                style={{ fontSize: isMobile ? "12px" : "13px", color: "#666" }}
              >
                {item.label}
              </div>
            </div>
          ))}
        </div>

        {/* ── CANONICAL VIEW ─────────────────────────────────────────────────── */}
        {usingCanonical &&
          reconciliation &&
          (() => {
            const rec = reconciliation;
            return (
              <>
                {/* Canonical summary cards */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                    gap: "16px",
                    marginBottom: "24px",
                  }}
                >
                  <div
                    style={{
                      padding: "20px",
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      borderRadius: "12px",
                      color: "white",
                      boxShadow: "0 4px 6px rgba(245,158,11,0.3)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        opacity: 0.9,
                        marginBottom: "8px",
                      }}
                    >
                      🔒 IN HOLD WINDOW
                    </div>
                    <div
                      style={{
                        fontSize: "30px",
                        fontWeight: "bold",
                        lineHeight: 1,
                      }}
                    >
                      {fmt(rec.heldTotal)}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        marginTop: "8px",
                        opacity: 0.9,
                      }}
                    >
                      {rec.held.length} holding
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "20px",
                      background:
                        "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                      borderRadius: "12px",
                      color: "white",
                      boxShadow: "0 4px 6px rgba(59,130,246,0.3)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        opacity: 0.9,
                        marginBottom: "8px",
                      }}
                    >
                      📤 QUEUED FOR TRANSFER
                    </div>
                    <div
                      style={{
                        fontSize: "30px",
                        fontWeight: "bold",
                        lineHeight: 1,
                      }}
                    >
                      {fmt(rec.releasedTotal)}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        marginTop: "8px",
                        opacity: 0.9,
                      }}
                    >
                      {rec.nextPayoutDate || "Awaiting next batch"}
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "20px",
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      borderRadius: "12px",
                      color: "white",
                      boxShadow: "0 4px 6px rgba(16,185,129,0.3)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        opacity: 0.9,
                        marginBottom: "8px",
                      }}
                    >
                      ✅ PAID THIS MONTH
                    </div>
                    <div
                      style={{
                        fontSize: "30px",
                        fontWeight: "bold",
                        lineHeight: 1,
                      }}
                    >
                      {fmt(rec.totalPaidThisMonth)}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        marginTop: "8px",
                        opacity: 0.9,
                      }}
                    >
                      {
                        rec.history.filter((h) => {
                          const d = new Date(h.payoutDate);
                          const n = new Date();
                          return (
                            d.getMonth() === n.getMonth() &&
                            d.getFullYear() === n.getFullYear()
                          );
                        }).length
                      }{" "}
                      payout(s)
                    </div>
                  </div>
                </div>

                {/* Held */}
                <div
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    onClick={() => toggleSection("held")}
                    style={{
                      padding: "16px 20px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: expanded.held
                        ? "1px solid #e5e7eb"
                        : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span style={{ fontSize: "20px" }}>🔒</span>
                      <div>
                        <div style={{ fontWeight: "600", fontSize: "16px" }}>
                          Held (In Hold Window)
                        </div>
                        <div style={{ fontSize: "13px", color: "#6b7280" }}>
                          {rec.held.length} entr
                          {rec.held.length !== 1 ? "ies" : "y"} ·{" "}
                          {fmt(rec.heldTotal)}
                        </div>
                      </div>
                    </div>
                    <span style={{ fontSize: "18px", color: "#6b7280" }}>
                      {expanded.held ? "▼" : "▶"}
                    </span>
                  </div>
                  {expanded.held && (
                    <div style={{ padding: "20px" }}>
                      {rec.held.length === 0 ? (
                        <p style={{ color: "#9ca3af", textAlign: "center" }}>
                          No earnings currently in hold period
                        </p>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                          }}
                        >
                          {rec.held.map((entry) => (
                            <div
                              key={entry._id}
                              style={{
                                padding: "16px",
                                background: "#fef3c7",
                                border: "2px solid #f59e0b",
                                borderRadius: "8px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    fontWeight: "600",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {entry.customerName}
                                </div>
                                {entry.autoReleaseAt && (
                                  <div
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: "600",
                                      color: "#92400e",
                                    }}
                                  >
                                    ⏱️ Releases in{" "}
                                    {formatCountdown(entry.autoReleaseAt)}
                                  </div>
                                )}
                                <div
                                  style={{ fontSize: "12px", color: "#78350f" }}
                                >
                                  Created:{" "}
                                  {entry.completedAt
                                    ? new Date(
                                        entry.completedAt,
                                      ).toLocaleDateString()
                                    : "—"}
                                </div>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div
                                  style={{
                                    fontWeight: "bold",
                                    fontSize: "18px",
                                    color: "#92400e",
                                  }}
                                >
                                  {fmt(entry.professionalEarnings)}
                                </div>
                                <div
                                  style={{ fontSize: "11px", color: "#78350f" }}
                                >
                                  Gross {fmt(entry.grossAmount)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Released */}
                <div
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    onClick={() => toggleSection("released")}
                    style={{
                      padding: "16px 20px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: expanded.released
                        ? "1px solid #e5e7eb"
                        : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span style={{ fontSize: "20px" }}>📤</span>
                      <div>
                        <div style={{ fontWeight: "600", fontSize: "16px" }}>
                          Released (Queued for Transfer)
                        </div>
                        <div style={{ fontSize: "13px", color: "#6b7280" }}>
                          {rec.released.length} entr
                          {rec.released.length !== 1 ? "ies" : "y"} ·{" "}
                          {fmt(rec.releasedTotal)}
                        </div>
                      </div>
                    </div>
                    <span style={{ fontSize: "18px", color: "#6b7280" }}>
                      {expanded.released ? "▼" : "▶"}
                    </span>
                  </div>
                  {expanded.released && (
                    <div style={{ padding: "20px" }}>
                      {rec.released.length === 0 ? (
                        <p style={{ color: "#9ca3af", textAlign: "center" }}>
                          No earnings currently queued for transfer
                        </p>
                      ) : (
                        <>
                          {rec.nextPayoutDate && (
                            <div
                              style={{
                                padding: "12px",
                                background: "#dbeafe",
                                borderRadius: "8px",
                                marginBottom: "16px",
                                fontSize: "14px",
                                color: "#1e40af",
                              }}
                            >
                                💡 These earnings will be included in your next weekly payout on{" "}
                                <strong>
                                  {`${new Date(rec.nextPayoutDate).toLocaleDateString("en-US", {
                                    month: "2-digit",
                                    day: "2-digit",
                                    year: "numeric",
                                  })} at 2:00 AM CT`}
                                </strong>
                            </div>
                          )}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "12px",
                            }}
                          >
                            {rec.released.map((entry) => (
                              <div
                                key={entry._id}
                                style={{
                                  padding: "16px",
                                  background: "#f0f9ff",
                                  border: "2px solid #3b82f6",
                                  borderRadius: "8px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div>
                                  <div
                                    style={{
                                      fontWeight: "600",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    ✅ {entry.customerName}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: "#6b7280",
                                    }}
                                  >
                                    Released:{" "}
                                    {entry.payoutReleasedAt
                                      ? new Date(
                                          entry.payoutReleasedAt,
                                        ).toLocaleString()
                                      : "—"}
                                  </div>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                  <div
                                    style={{
                                      fontWeight: "bold",
                                      fontSize: "18px",
                                      color: "#1e40af",
                                    }}
                                  >
                                    {fmt(entry.professionalEarnings)}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#6b7280",
                                    }}
                                  >
                                    Gross {fmt(entry.grossAmount)}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* History */}
                <div
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    marginBottom: "24px",
                  }}
                >
                  <div
                    onClick={() => toggleSection("history")}
                    style={{
                      padding: "16px 20px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: expanded.history
                        ? "1px solid #e5e7eb"
                        : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span style={{ fontSize: "20px" }}>📜</span>
                      <div>
                        <div style={{ fontWeight: "600", fontSize: "16px" }}>
                          Payout History
                        </div>
                        <div style={{ fontSize: "13px", color: "#6b7280" }}>
                          {rec.history.length} completed payout
                          {rec.history.length !== 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>
                    <span style={{ fontSize: "18px", color: "#6b7280" }}>
                      {expanded.history ? "▼" : "▶"}
                    </span>
                  </div>
                  {expanded.history && (
                    <div style={{ padding: "20px" }}>
                      {rec.history.length === 0 ? (
                        <p style={{ color: "#9ca3af", textAlign: "center" }}>
                          No payout history yet
                        </p>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                          }}
                        >
                          {rec.history.map((h, idx) => (
                            <div
                              key={idx}
                              style={{
                                padding: "16px",
                                background: "#f9fafb",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    fontWeight: "600",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {new Date(h.payoutDate).toLocaleDateString(
                                    "en-US",
                                    {
                                      weekday: "short",
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    },
                                  )}
                                </div>
                                <div
                                  style={{ fontSize: "13px", color: "#6b7280" }}
                                >
                                  {h.entryCount} earning
                                  {h.entryCount !== 1 ? "s" : ""} settled
                                </div>
                              </div>
                              <div
                                style={{
                                  fontWeight: "bold",
                                  fontSize: "20px",
                                  color: "#059669",
                                }}
                              >
                                ${(h.amount || 0).toFixed(2)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            );
          })()}

        {/* ── Recent Payouts table ───────────────────────────────────────────── */}
        {!usingCanonical && (
          <div
            className="dashboard-card"
            style={{
              padding: isMobile ? "20px" : "25px",
              marginBottom: "25px",
            }}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "20px",
                fontSize: isMobile ? "18px" : "20px",
              }}
            >
              Recent Payouts
            </h3>
            {payouts.length === 0 ? (
              <div
                style={{ textAlign: "center", padding: "30px", color: "#999" }}
              >
                <div style={{ fontSize: "36px", marginBottom: "10px" }}>💳</div>
                <p>No payouts yet</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: isMobile ? "13px" : "14px",
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                      <th
                        style={{
                          padding: isMobile ? "10px 8px" : "12px",
                          textAlign: "left",
                          fontWeight: "600",
                        }}
                      >
                        Date
                      </th>
                      <th
                        style={{
                          padding: isMobile ? "10px 8px" : "12px",
                          textAlign: "left",
                          fontWeight: "600",
                        }}
                      >
                        Amount
                      </th>
                      <th
                        style={{
                          padding: isMobile ? "10px 8px" : "12px",
                          textAlign: "left",
                          fontWeight: "600",
                        }}
                      >
                        Status
                      </th>
                      {!isMobile && (
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "left",
                            fontWeight: "600",
                          }}
                        >
                          Method
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.slice(0, 5).map((payout, idx) => (
                      <tr
                        key={idx}
                        style={{ borderBottom: "1px solid #f0f0f0" }}
                      >
                        <td style={{ padding: isMobile ? "10px 8px" : "12px" }}>
                          {new Date(payout.date).toLocaleDateString()}
                        </td>
                        <td
                          style={{
                            padding: isMobile ? "10px 8px" : "12px",
                            fontWeight: "600",
                            color: "#001d4a",
                          }}
                        >
                          ${payout.amount?.toFixed(2) || "0.00"}
                        </td>
                        <td style={{ padding: isMobile ? "10px 8px" : "12px" }}>
                          <span
                            style={{
                              padding: "3px 10px",
                              borderRadius: "12px",
                              background:
                                payout.status === "completed"
                                  ? "#10b98120"
                                  : "#f59e0b20",
                              color:
                                payout.status === "completed"
                                  ? "#10b981"
                                  : "#f59e0b",
                              fontSize: "11px",
                              fontWeight: "600",
                              textTransform: "capitalize",
                            }}
                          >
                            {payout.status}
                          </span>
                        </td>
                        {!isMobile && (
                          <td style={{ padding: "12px", color: "#666" }}>
                            {payout.method || "Bank Transfer"}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Recent Transactions — always shown */}
        <div
          className="dashboard-card"
          style={{ padding: isMobile ? "20px" : "25px" }}
        >
          <h3
            style={{
              marginTop: 0,
              marginBottom: "20px",
              fontSize: isMobile ? "18px" : "20px",
            }}
          >
            Recent Transactions
          </h3>
          {transactions.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "30px", color: "#999" }}
            >
              <div style={{ fontSize: "36px", marginBottom: "10px" }}>📝</div>
              <p>No transactions yet</p>
            </div>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {transactions.slice(0, 10).map((txn, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: isMobile ? "12px" : "15px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: isMobile ? "wrap" : "nowrap",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{ flex: 1, minWidth: isMobile ? "100%" : "auto" }}
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: isMobile ? "14px" : "15px",
                        marginBottom: "4px",
                      }}
                    >
                      {txn.description || "Booking Payment"}
                    </div>
                    <div
                      style={{
                        fontSize: isMobile ? "12px" : "13px",
                        color: "#666",
                      }}
                    >
                      {new Date(txn.date).toLocaleDateString()} at{" "}
                      {new Date(txn.date).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "16px" : "18px",
                      fontWeight: "700",
                      color: txn.type === "credit" ? "#10b981" : "#ef4444",
                    }}
                  >
                    {txn.type === "credit" ? "+" : "-"}$
                    {txn.amount?.toFixed(2) || "0.00"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerEarnings;
